import { skipToken } from "@reduxjs/toolkit/query";
import {
  LoadingState,
  type TranscriptFragment,
} from "@repo/app-types/client/client";
import type { InterviewLanguage } from "@repo/app-types/constants/languages";
import type { Activity } from "@repo/app-types/entities/activity";
import type { ComplexAnswer } from "@repo/app-types/entities/answer";
import type { Attachment } from "@repo/app-types/entities/attachment";
import type { Contact } from "@repo/app-types/entities/contact";
import {
  getNextMeetingAvailabilityDatesDisplay,
  type InterviewCheatingRiskSummary,
  type InterviewLoaded,
  isCompletedCallMetadata,
} from "@repo/app-types/entities/interview";
import type { AdminApiInterviewSectionResult } from "@repo/app-types/entities/interviewSection/result";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FileCard } from "@repo/ui/Cards/fileCard";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalCloseButton } from "@repo/ui/Modal/modalCloseButton";
import { TextSkeleton } from "@repo/ui/Skeletons/skeletons";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import { IconShare2, IconSparkles, IconStar } from "@tabler/icons-react";
import { type FC, useEffect, useState } from "react";
import { Dialog } from "react-aria-components";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useInterviewCallMediaUrlsQuery } from "../../../api/entities/interview.js";
import { AUDIO_URL_REFRESH_INTERVAL_MS } from "../../../api/entities/recording.js";
import { getIsInFountainIntegration } from "../../../helpers/utilities.js";
import { AssessmentFeedbackWidget } from "../../insights/assessmentFeedback/widget.js";
import { LabeledAssessmentScore } from "../../insights/assessmentScore.js";
import { CheatingRiskSummary } from "../../insights/cheatingRiskSummary.js";
import { ContactNameWithTime } from "../../insights/contactNameWithTime.js";
import { ContactUserCustomFieldsSection } from "./contactUserCustomFieldsSection.js";
import { InterviewAssessmentSection } from "./interviewAssessmentSection.js";
import { InterviewDataExtractionSection } from "./interviewDataExtractionSection.js";
import { InterviewDecisionButtons } from "./interviewDecisionButtons.js";
import { InterviewMoreMenu } from "./interviewMoreMenu.js";
import { InterviewSummary } from "./interviewSummary.js";
import { InterviewTranscript } from "./interviewTranscript.js";
import { InterviewUserCustomFieldsSection } from "./interviewUserCustomFieldsSection.js";
import { PreInterviewFormValuesSummary } from "./preInterviewFormValuesSummary.js";
import { SentimentSection } from "./sentimentSection.js";
import { ShareInterviewModalContent } from "./shareInterviewModal.js";
import { SurveyResponsesSection } from "./surveyResponses/surveyResponsesSection.js";

interface InterviewSlideoverProps {
  projectId: string;
  interviewId: string | undefined;
  interview: InterviewLoaded | undefined;
  contact: Contact | undefined;
  onClose: () => void;
}

export const InterviewSlideover: FC<InterviewSlideoverProps> = ({
  projectId,
  interviewId,
  contact,
  interview,
  onClose,
}) => {
  const isInFountainIntegration = getIsInFountainIntegration();

  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.LOADING,
  );
  const [interviewSectionResults, setInterviewSectionResults] = useState<
    readonly AdminApiInterviewSectionResult[]
  >([]);
  const [transcriptFragments, setTranscriptFragments] = useState<
    TranscriptFragment[]
  >([]);
  const [highlightedTranscriptFragment, setHighlightedTranscriptFragment] =
    useState<string | null>(null);

  const [complexAnswers, setComplexAnswers] = useState<ComplexAnswer[]>([]);
  const [contactAttachments, setContactAttachments] = useState<Attachment[]>(
    [],
  );
  const [cheatingRiskSummary, setCheatingRiskSummary] =
    useState<InterviewCheatingRiskSummary | null>(null);
  const [outreachActivities, setOutreachActivities] = useState<
    readonly Activity[]
  >([]);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [transcriptFragmentToShare, setTranscriptFragmentToShare] =
    useState<TranscriptFragment | null>(null);
  const [targetLanguage, setTargetLanguage] =
    useState<InterviewLanguage | null>(null);

  // Load transcriptFragments for this interview and files for this contact.
  useEffect(() => {
    if (!interview || !contact) {
      setContactAttachments([]); // Clear the attachments state when slideover closes.
      setCheatingRiskSummary(null); // Clear the cheating risk summary state when slideover closes.
      setOutreachActivities([]); // Clear the outreach activities state when slideover closes.
      return;
    }

    const fetchData = async () => {
      setLoadingState(LoadingState.LOADING);

      try {
        const axios = await getAxiosInstanceWithAuth();
        const [insightsResponse, attachmentsResponse] = await Promise.all([
          axios.get(`/insights/interviews/${interview.id}`, {
            params: targetLanguage
              ? { target_language: targetLanguage }
              : undefined,
          }),
          axios.get(`/attachment/list-for-contact/${contact.id}`),
        ]);

        const insightsData = insightsResponse.data;
        const attachmentsData = attachmentsResponse.data;

        setInterviewSectionResults(insightsData.interview_section_results);
        setTranscriptFragments(insightsData.transcript_fragments);

        if (insightsData.complex_answers.length > 0) {
          setComplexAnswers(insightsData.complex_answers);
        }

        setContactAttachments(attachmentsData);

        setCheatingRiskSummary(insightsData.cheating_risk_summary);
        setOutreachActivities([...(insightsData.outreach_activities || [])]);

        setLoadingState(LoadingState.LOADED);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoadingState(LoadingState.ERROR);
      }
    };

    fetchData();
  }, [interview?.id, contact?.id, targetLanguage]);

  const { data: callMediaUrls } = useInterviewCallMediaUrlsQuery(
    interviewId ? { projectId, interviewId } : skipToken,
    // Media URLs are presigned with a limited expiry
    { pollingInterval: AUDIO_URL_REFRESH_INTERVAL_MS },
  );

  const findFragmentFromText = (text: string) => {
    // Convert to lower case and remove any trailing periods.
    const trimmedText = text.toLowerCase().replace(/\.$/, "");

    return transcriptFragments.find(
      (f) =>
        f.text_transcript &&
        f.text_transcript.toLowerCase().includes(trimmedText),
    );
  };

  const jumpToFragmentByText = (text: string) => {
    const fragment = findFragmentFromText(text);
    if (fragment) {
      setHighlightedTranscriptFragment(fragment.id);
    }
  };

  const onShareSentimentTextClick = (text: string) => {
    const fragment = findFragmentFromText(text);
    if (fragment) {
      setIsShareModalOpen(true);
      setTranscriptFragmentToShare(fragment);
    }
  };

  const handleAttachmentDownload = async (attachmentId: string) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.get(
        `/attachment/presigned-url/${attachmentId}`,
      );
      const { url } = response.data;

      if (url) {
        window.open(url, "_blank");
      }
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const candidateNextMeetingAvailabilityDatesDisplay =
    interview && getNextMeetingAvailabilityDatesDisplay(interview.metadata);

  const renderSlideoverContent = () => {
    return (
      <div className="flex flex-col px-6">
        <div className="sticky top-0 z-10 space-y-4 border-b border-border-light bg-white py-4">
          <div className="flex items-center justify-between">
            {renderHeaderLeft()}
            {isInFountainIntegration ? null : (
              <div className="flex items-center space-x-3">
                {renderHeaderRight()}
                <ModalCloseButton onPress={onClose} />
              </div>
            )}
          </div>
        </div>
        {renderMainContent()}
      </div>
    );
  };

  const renderHeaderLeft = () => {
    // Show a skeleton when the interview or contact has not loaded yet.
    if (!interview || !contact) {
      return (
        <div>
          <div className="text-md w-48 animate-pulse rounded-full bg-gray-200">
            &nbsp;
          </div>
          <div className="text-sm">&nbsp;</div>
        </div>
      );
    }

    return (
      <ContactNameWithTime
        contact={contact}
        date={interview.started_at ? new Date(interview.started_at) : undefined}
      />
    );
  };

  const renderHeaderRight = () => {
    if (!interview || !contact) {
      return null;
    }

    return (
      <>
        {["in_progress", "completed"].includes(interview.status) ? (
          <InterviewDecisionButtons interview={interview} />
        ) : null}
        <Button
          variant={ButtonVariant.Secondary}
          onClick={() => {
            setIsShareModalOpen(true);
          }}
          size={Size.MEDIUM}
          icon={
            <IconShare2 className="text-white-900 h-4 w-4" aria-hidden="true" />
          }
        >
          Share
        </Button>
        <InterviewMoreMenu
          interview={interview}
          onInterviewDeleteDone={onClose}
        />
      </>
    );
  };

  const renderMainContent = () => {
    // Show a skeleton when the interview or contact has not loaded yet.
    if (!interview || !contact) {
      return (
        <div className="my-5 space-y-5">
          <TextSkeleton />
          <TextSkeleton />
        </div>
      );
    }

    const { insights, metadata } = interview;
    const preInterviewFormSubmission =
      metadata?.pre_interview_form ?? undefined;

    return (
      <div className="overflow-auto pb-6">
        {contactAttachments.length > 0 && (
          <>
            <Label size={Size.MEDIUM} className="mt-4 mb-2">
              Resume
            </Label>
            <div className="flex flex-wrap gap-2">
              {contactAttachments.map((attachment) => (
                <FileCard
                  key={attachment.id}
                  name={`${
                    attachment.type.charAt(0).toUpperCase() +
                    attachment.type.slice(1)
                  } ${new Date(attachment.created_at).toLocaleString()}`}
                  onDownload={() => handleAttachmentDownload(attachment.id)}
                />
              ))}
            </div>
          </>
        )}

        <PreInterviewFormValuesSummary
          formSubmission={preInterviewFormSubmission}
        />

        <div className="mt-4 mb-3 flex items-center space-x-2">
          <Label size={Size.MEDIUM}>
            {insights?.assessment_score ? "Assessment" : "Summary"}
          </Label>
          <IconSparkles className="size-4 text-purple-600" />
          <AssessmentFeedbackWidget interview={interview} />
        </div>
        {insights?.assessment_score ? (
          <div className="mb-4 flex gap-2">
            <LabeledAssessmentScore
              score={
                insights.assessment_score_measurement ??
                insights.assessment_score
              }
              size={Size.MEDIUM}
            />
            <CheatingRiskSummary
              interview={interview}
              cheatingRiskSummary={cheatingRiskSummary}
            />
          </div>
        ) : null}
        {insights ? <InterviewSummary insights={insights} /> : null}
        {insights?.sentiment_score ? (
          <>
            <Label size={Size.SMALL} className="mt-4 mb-2">
              Sentiment <IconSparkles className="ml-1 h-4 w-4" />
            </Label>
            <SentimentSection
              insights={insights}
              onClickText={jumpToFragmentByText}
              onClickShare={onShareSentimentTextClick}
            />
          </>
        ) : null}
        <InterviewAssessmentSection
          interview={interview}
          interviewSectionResults={interviewSectionResults}
        />
        <InterviewDataExtractionSection interview={interview} />
        {interview.questions ? (
          <SurveyResponsesSection
            interviewQuestions={interview.questions}
            complexAnswers={complexAnswers}
          />
        ) : null}
        <ContactUserCustomFieldsSection contact={contact} />
        <InterviewUserCustomFieldsSection interview={interview} />
        {candidateNextMeetingAvailabilityDatesDisplay && (
          <div className="mt-4 mb-2">
            <Label size={Size.MEDIUM}>
              Candidate availability for next meeting
            </Label>
            <div className="mt-4 max-w-2xl overflow-hidden border border-border-light sm:rounded-lg">
              <table className="min-w-full table-fixed divide-y divide-border-light">
                <thead className="group bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="w-[80%] px-3 py-2 text-left text-xs font-medium text-muted-foreground"
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="w-[20%] px-3 py-2 text-left text-xs font-medium text-muted-foreground"
                    >
                      Preferred
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-light bg-white">
                  {candidateNextMeetingAvailabilityDatesDisplay.map(
                    (displayDate) => (
                      <tr key={displayDate.date} className={`group relative`}>
                        <td className={`px-4 py-2 text-sm text-foreground`}>
                          {displayDate.date}
                        </td>
                        <td className="px-4 py-2 text-sm text-foreground">
                          {displayDate.is_preferred && (
                            <IconStar className="h-5 w-5 text-yellow-400" />
                          )}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <Label size={Size.MEDIUM} className="mt-4 mb-2">
          Transcript
        </Label>
        <div className="flex flex-col">
          <InterviewTranscript
            loadingState={loadingState}
            interviewSectionResults={interviewSectionResults}
            transcriptFragments={transcriptFragments}
            highlightedTranscriptFragment={highlightedTranscriptFragment}
            onClickShare={(fragment) => {
              setIsShareModalOpen(true);
              setTranscriptFragmentToShare(fragment);
            }}
            onHighlightFragment={setHighlightedTranscriptFragment}
            calls={
              interview.metadata?.calls?.filter(isCompletedCallMetadata) || []
            }
            callMediaUrls={callMediaUrls}
            onClickTranslate={(language) => {
              setTargetLanguage(language);
            }}
            interviewInsights={insights}
            jumpToFragmentByText={jumpToFragmentByText}
            outreachActivities={outreachActivities}
          />
        </div>
      </div>
    );
  };

  const renderShareModal = () => {
    if (!interview || !contact) {
      return null;
    }

    const closeShareModal = () => {
      setIsShareModalOpen(false);
      setTranscriptFragmentToShare(null);
    };

    return (
      <Modal isOpen={isShareModalOpen} onCancel={closeShareModal}>
        <ShareInterviewModalContent
          onClose={closeShareModal}
          interview={interview}
          transcriptFragment={transcriptFragmentToShare}
          contact={contact}
        />
      </Modal>
    );
  };

  if (isInFountainIntegration) {
    return (
      <>
        <div className="fixed inset-0 overflow-x-hidden overflow-y-scroll bg-white">
          {renderSlideoverContent()}
        </div>
        {renderShareModal()}
      </>
    );
  }

  // Open the slideover as soon as an interview was selected
  const isOpen = interviewId !== undefined;

  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  };

  return (
    <>
      <SlideoverContainer
        isDismissable
        isOpen={isOpen}
        onOpenChange={onOpenChange}
      >
        <Dialog
          aria-label="Interview details"
          className="flex h-full flex-col overflow-y-auto"
        >
          {renderSlideoverContent()}
        </Dialog>
      </SlideoverContainer>
      {renderShareModal()}
    </>
  );
};
